import {
    Availability,
    AvailabilityStatus,
    type DateRange,
    Repeat,
    availabilityColor,
    availabilityIcon,
    availabilityLabel,
    dateAdd,
    formatDate,
    formatTimeDistanceShort,
    getRange,
    isAbortError,
    toDateString,
    toTimeString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/availability-dialog";
import type { AvailabilityDialog } from "../../../elements/availability-dialog";
import "../../../elements/date-range-picker";
import "../../../elements/month-picker";
import "../../../elements/scroller";
import { app } from "../../../init";
import { LatestRequest } from "../../../lib/latest-request";
import { singleton } from "../../../lib/singleton";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "./components/calendar";
import type { CalendarEntry } from "./components/calendar";

const repeatLabel: Record<Repeat, string> = {
    [Repeat.Never]: "nie",
    [Repeat.Weekly]: "wöchentlich",
    [Repeat.Monthly]: "monatlich",
    [Repeat.Yearly]: "jährlich",
};

@customElement("ptc-employees-availabilities-single")
export class EmployeesAvailabilitiesSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<id>\d+)\/availabilities/;

    @routeProperty({ arg: "id", type: Number })
    employeeId: number;

    defaultRange = () => {
        const from = getRange(new Date(), "month").from;
        return { from, to: dateAdd(from, { months: 3 }) };
    };

    rangeChanged = () => this._load();

    get routeTitle() {
        return `Verfügbarkeiten: ${this._employee && this._employee.name}`;
    }

    async handleRoute() {
        if (!this.date) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }
        await this._load();
    }

    @state()
    private _loading = false;

    private _request = new LatestRequest(this);

    @state()
    private _availabilities: Availability[] = [];

    @singleton("ptc-availability-dialog")
    private _availabilityDialog: AvailabilityDialog;

    private get _employee() {
        return (this.employeeId && app.getEmployee(this.employeeId)) || null;
    }

    private async _load() {
        if (!this._employee || !this.dateRange) {
            return;
        }

        this._loading = true;

        try {
            this._availabilities = await app.api.getAvailabilities(
                { employees: [this.employeeId], ...this.dateRange },
                { signal: this._request.start() }
            );
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _newAvailability(date: DateString) {
        const created = await this._availabilityDialog.show(
            new Availability({
                employeeId: this.employeeId,
                status: AvailabilityStatus.Available,
                date,
            })
        );
        if (created) {
            // Update data in the background if new availability was created
            void this._load();
        }
    }

    private _renderAge(av: Availability) {
        if (av.repeats !== Repeat.Never) {
            return { icon: "icon.object.history", title: `Wiederholt sich ${repeatLabel[av.repeats]}` };
        }
        if (!av.updated) {
            return undefined;
        }
        return {
            text: formatTimeDistanceShort(av.updated),
            icon: "icon.action.edit",
            title: `Zuletzt bearbeitet am ${formatDate(av.updated)} ${toTimeString(av.updated)}`,
        };
    }

    private async _editAvailability(av: Availability) {
        const edited = await this._availabilityDialog.show(av);
        if (edited) {
            // Update data in the background if availability was edited
            void this._load();
        }
    }

    static styles = [
        shared,
        css`
            .absence {
                border: solid 1px var(--shade-2);
                border-radius: 1em;
                padding-top: 0.5em;
            }

            .days-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                grid-gap: 0.5em 0;
            }

            .day {
                text-align: center;
                height: 2em;
                box-sizing: border-box;
                position: relative;
            }

            .day.start,
            .day.middle,
            .day.end {
                border-top: solid 1px var(--color-highlight);
                border-bottom: solid 1px var(--color-highlight);
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            .day.start {
                border-left: solid 1px var(--color-highlight);
                border-top-left-radius: 0.5em;
                border-bottom-left-radius: 0.5em;
            }

            .day.end {
                border-right: solid 1px var(--color-highlight);
                border-top-right-radius: 0.5em;
                border-bottom-right-radius: 0.5em;
            }

            .absence.denied .day,
            .absence.requested .day {
                color: var(--color-highlight);
                background: transparent;
            }

            .absence.denied .day:not(.before):not(.after)::after {
                content: "";
                display: block;
                width: 110%;
                height: 1px;
                background: currentColor;
                position: absolute;
                top: 0;
                bottom: 0;
                margin: auto;
                left: -5%;
            }

            .today {
                background: var(--color-highlight);
                color: var(--color-bg);
                border-radius: 100%;
                border: solid 1px;
                width: 1.5em;
                height: 1.5em;
                line-height: 1.5em;
                font-weight: bold;
            }
        `,
    ];

    render() {
        const emp = this._employee;
        if (!emp) {
            return;
        }

        return html`
            <div class="fullbleed vertical layout">
                <div class="padded center-aligning horizontal layout border-bottom">
                    <ptc-date-range-picker
                        @range-selected=${(e: CustomEvent<DateRange>) =>
                            this.go(null, { ...this.router.params, ...e.detail })}
                        .range=${this.dateRange}
                    ></ptc-date-range-picker>
                </div>

                <div class="stretch vertical scroller">
                    <ptc-calendar
                        .dateRange=${this.dateRange}
                        .entries=${this._availabilities.map((av) => ({
                            start: av.date,
                            end: dateAdd(av.date, { days: 1 }),
                            icon: availabilityIcon(av.status),
                            time: av.start || av.end ? { start: av.start, end: av.end } : undefined,
                            title: av.comment || availabilityLabel(av.status),
                            color: availabilityColor(av.status),
                            trailing: this._renderAge(av),
                            data: av,
                        }))}
                        @entry-clicked=${(e: CustomEvent<{ entry: CalendarEntry<Availability> }>) =>
                            this._editAvailability(e.detail.entry.data!)}
                        @day-clicked=${(e: CustomEvent<{ date: DateString }>) => this._newAvailability(e.detail.date)}
                        .renderDay=${(date: string) => {
                            const contract = this._employee?.getContractForDate(date);
                            const blocked = (!contract || contract.blocked) && (contract?.comment || "inaktiv");
                            return blocked
                                ? html`<div class="fullbleed faded centering layout" title="${blocked}">
                                      <ptc-ui-icon name="icon.status.blocked" class="giant faded"></ptc-ui-icon>
                                  </div>`
                                : undefined;
                        }}
                    ></ptc-calendar>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
